/// <amd-module name="Repos/Tfvc/Views/FilesHubActions/Components/NewFileDialog" />
import * as React from "react";
import { getInvalidFileNameMessage } from "Repos/Common/Util/File";
import * as Resources from "Repos/Tfvc/Views/FilesHubActions/Resources";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IButtonProps } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode } from "VSSUI/Util";

export interface INewFileDialogProps {
    errorMessage: IReadonlyObservableValue<string>;
    onDismiss: (name?: string) => void;
    parentPath: string;
}

export class NewFileDialog extends React.Component<INewFileDialogProps> {
    private name = new ObservableValue<string>("");
    private nameMessage = new ObservableValue<string>(Resources.NewFileNameDefaultMessage);
    private nameMessageIsError = new ObservableValue<boolean>(false);
    private fieldsValid = new ObservableValue<boolean>(false);

    public render() {
        let parentPath = this.props.parentPath;
        if (!parentPath.endsWith("/")) {
            parentPath += "/";
        }

        return (
            <Observer valid={this.fieldsValid}>
                {(props: { valid: boolean }) => {
                    const { onDismiss } = this.props;
                    const buttons: IButtonProps[] = [
                        {
                            onClick: () => onDismiss(),
                            text: Resources.Cancel
                        },
                        {
                            onClick: this.submit,
                            disabled: !props.valid,
                            primary: true,
                            text: Resources.Create
                        }
                    ];
                    return (
                        <Dialog
                            defaultActiveElement={".repos-new-file-dialog-path-input"}
                            onDismiss={onDismiss}
                            titleProps={{ text: Resources.NewFile }}
                            footerButtonProps={buttons}
                        >
                            <div className="rhythm-vertical-16 flex-grow">
                                <Observer errorMessage={this.props.errorMessage}>
                                    {(props: { errorMessage: string }) => {
                                        return (
                                            props.errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>
                                        );
                                    }}
                                </Observer>
                                <FormItem label={Resources.NewFileName} error={this.nameMessageIsError} message={this.nameMessage}>
                                    <TextField
                                        onChange={this.onNameChanged}
                                        onKeyDown={this.onKeyDown}
                                        prefixIconProps={{
                                            render: () => (
                                                <span className="repos-new-file-dialog-path-prefix flex-row flex-center">
                                                    <Tooltip overflowOnly={true} text={parentPath}>
                                                        <span className="text-ellipsis">{parentPath}</span>
                                                    </Tooltip>
                                                </span>
                                            )
                                        }}
                                        value={this.name}
                                        inputClassName="repos-new-file-dialog-path-input"
                                    />
                                </FormItem>
                            </div>
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }

    private submit = (): void => {
        this.props.onDismiss(this.name.value);
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (ev.which === KeyCode.enter && this.fieldsValid.value) {
            this.submit();
        }
    };

    private onNameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string): void => {
        this.name.value = name;

        let message = Resources.NewFileNameDefaultMessage;
        let messageIsError = false;

        const trimmed = name.trim();
        if (!trimmed || trimmed.endsWith("/")) {
            message = Resources.NewFileNameCannotBeEmpty;
            messageIsError = true;
        } else {
            const parts = name.split("/");
            let path = "/";
            for (const part of parts) {
                if (!part.trim()) {
                    message = format(Resources.NewFileFolderNameEmptyFormat, path);
                    messageIsError = true;
                    break;
                }

                const invalidNameMessage = getInvalidFileNameMessage(part);
                if (invalidNameMessage) {
                    message = invalidNameMessage;
                    messageIsError = true;
                    break;
                }
                path += part + "/";
            }
        }

        this.nameMessage.value = message;
        this.nameMessageIsError.value = messageIsError;

        this.updateFieldsValid();
    };

    private updateFieldsValid() {
        const valid = !!this.name.value.trim() && !this.nameMessageIsError.value;

        if (this.fieldsValid.value !== valid) {
            this.fieldsValid.value = valid;
        }
    }
}
